import {
  DIMACON_COLORS,
  DIMACON_ICON_SHAPES,
  DIMACON_ICON_VIEWBOX,
  DIMACON_IMPRINT_URL,
  DIMACON_LOGO_SHAPES,
  DIMACON_LOGO_VIEWBOX,
  DIMACON_PRIVACY_POLICY_URL,
  DIMACON_WEBSITE_URL,
} from "./dimaconBrand";
import {
  MIRAGON_DARK,
  MIRAGON_FIXED,
  MIRAGON_IMPRINT_URL,
  MIRAGON_KOMET_PATH,
  MIRAGON_LIGHT,
  MIRAGON_WEBSITE_URL,
  MIRAGON_WORDMARK_SHAPES,
  MIRAGON_WORDMARK_VIEWBOX,
} from "./miragonBrand";

/**
 * Tenants of the Miragon fork (see FORK_CHANGES.md). Miragon runs the instance; partner companies sign in with their
 * own Entra tenant and must not appear under Miragon's brand. Every tenant uses Miragon's design system and only
 * fills its placeholders: the name in page titles and e-mails, logo, legal links and the colors that carry the
 * brand. Accounts belong to a tenant by the domain of their e-mail address, teams by a setting of the instance
 * admins; everything else, including the sign-in pages, shows Miragon, which runs the instance.
 */
export const TENANT_IDS = ["miragon", "dimacon"] as const;
export type TenantId = (typeof TENANT_IDS)[number];
export const DEFAULT_TENANT_ID: TenantId = "miragon";

type LogoShapes = ReadonlyArray<{ type: "path" | "polygon"; value: string }>;

/**
 * The CSS variables that carry the brand. packages/config/theme/miragon sets Miragon's values; a tenant replaces all
 * of them in light and dark mode, so that a light value never wins over a dark default.
 */
export const TENANT_CSS_VARIABLES = [
  "--marke-primaer",
  "--marke-primaer-hover",
  "--marke-primaer-text",
  "--marke-akzent",
  "--cd-ui-hover",
  "--cd-ui-link",
  "--cd-ui-fokus",
  "--cd-ui-interaktion",
  "--cd-ui-icon-akzent",
  "--cd-ui-wortmarke",
] as const;
type TenantCss = Record<(typeof TENANT_CSS_VARIABLES)[number], string>;

export interface TenantBranding {
  id: TenantId;
  /** The company, for alt texts and labels */
  companyName: string;
  /** Page titles and e-mails name the app like this; everywhere else it is APP_NAME */
  appName: string;
  /** Accounts whose e-mail address is on one of these domains belong to the tenant */
  emailDomains: readonly string[];
  websiteUrl: string;
  privacyPolicyUrl: string;
  imprintUrl: string;
  /** Brand colors of accounts and teams that have none of their own (upstream's brandColor and darkBrandColor) */
  brandColor: string;
  darkBrandColor: string;
  /** The app icon next to the name in the sidebar, a file in apps/web/public */
  iconPath: string;
  wordmark: { viewBox: string; shapes: LogoShapes };
  /** Replaces Miragon's values of TENANT_CSS_VARIABLES; null for Miragon, whose values are the defaults */
  css: { light: TenantCss; dark: TenantCss } | null;
  /** E-mails reach no CSS variables: the raster word mark (Outlook shows no SVG), button and link colors */
  email: { logoPath: string; brand: string; link: string };
  /** Link previews: the ground, the word mark and text on it, and the accent shape at the right edge (in pixels) */
  og: {
    backgroundColor: string;
    backgroundImage?: string;
    wordmark: string;
    text: string;
    accent: {
      viewBox: string;
      shapes: LogoShapes;
      color: string;
      width: number;
      height: number;
      right: number;
      bottom: number;
    };
  };
}

const WHITE = MIRAGON_FIXED["--cd-weiss"];

// Miragon's values as constants.ts has them (tenantBranding.test.ts keeps them equal). The register does not import
// constants.ts, because many tests mock that module with only the values they need.
const MIRAGON_BRAND_COLOR = MIRAGON_FIXED["--cd-blau"].toLowerCase();
const MIRAGON_ICON_PATH = "/miragon-komet-blau.svg";
// biome-ignore lint/correctness/noProcessGlobal: Next.js only inlines NEXT_PUBLIC_* read from the global process
const MIRAGON_PRIVACY_POLICY_URL =
  process.env.NEXT_PUBLIC_WEBSITE_PRIVACY_POLICY_URL || "https://cal.com/privacy";

export const TENANT_BRANDINGS: Record<TenantId, TenantBranding> = {
  miragon: {
    id: "miragon",
    companyName: "Miragon",
    appName: "Miragon Cal",
    emailDomains: ["miragon.io"],
    websiteUrl: MIRAGON_WEBSITE_URL,
    privacyPolicyUrl: MIRAGON_PRIVACY_POLICY_URL,
    imprintUrl: MIRAGON_IMPRINT_URL,
    brandColor: MIRAGON_BRAND_COLOR,
    darkBrandColor: MIRAGON_BRAND_COLOR,
    iconPath: MIRAGON_ICON_PATH,
    wordmark: { viewBox: MIRAGON_WORDMARK_VIEWBOX, shapes: MIRAGON_WORDMARK_SHAPES },
    css: null,
    email: {
      logoPath: "/emails/miragon-logo.png",
      brand: MIRAGON_FIXED["--cd-blau"],
      link: MIRAGON_LIGHT["--cd-ui-link"],
    },
    og: {
      backgroundColor: MIRAGON_FIXED["--cd-blau"],
      wordmark: WHITE,
      text: WHITE,
      accent: {
        viewBox: "0 0 288 90",
        shapes: [{ type: "path", value: MIRAGON_KOMET_PATH }],
        color: MIRAGON_FIXED["--cd-gruen"],
        width: 560,
        height: 176,
        right: -60,
        bottom: 110,
      },
    },
  },
  dimacon: {
    id: "dimacon",
    companyName: "Dimacon",
    appName: "Dimacon Cal",
    emailDomains: ["dimacon.io"],
    websiteUrl: DIMACON_WEBSITE_URL,
    privacyPolicyUrl: DIMACON_PRIVACY_POLICY_URL,
    imprintUrl: DIMACON_IMPRINT_URL,
    // dimacon.io draws its buttons dark with white text and uses its orange as the accent
    brandColor: DIMACON_COLORS.dunkel,
    darkBrandColor: DIMACON_COLORS.hell,
    iconPath: "/tenants/dimacon-icon.svg",
    wordmark: { viewBox: DIMACON_LOGO_VIEWBOX, shapes: DIMACON_LOGO_SHAPES },
    css: {
      light: {
        "--marke-primaer": DIMACON_COLORS.dunkel,
        "--marke-primaer-hover": DIMACON_COLORS.text,
        "--marke-primaer-text": WHITE,
        "--marke-akzent": DIMACON_COLORS.orange,
        "--cd-ui-hover": DIMACON_COLORS.orangeFlaeche,
        "--cd-ui-link": DIMACON_COLORS.orangeText,
        "--cd-ui-fokus": DIMACON_COLORS.orangeText,
        "--cd-ui-interaktion": DIMACON_COLORS.orangeText,
        "--cd-ui-icon-akzent": DIMACON_COLORS.orange,
        "--cd-ui-wortmarke": DIMACON_COLORS.logo,
      },
      dark: {
        "--marke-primaer": DIMACON_COLORS.hell,
        "--marke-primaer-hover": DIMACON_COLORS.linie,
        "--marke-primaer-text": DIMACON_COLORS.dunkel,
        "--marke-akzent": DIMACON_COLORS.orange,
        // Dark mode has no tints, the hover surface stays Miragon's neutral step
        "--cd-ui-hover": MIRAGON_DARK["--cd-ui-hover"],
        "--cd-ui-link": DIMACON_COLORS.orangeHell,
        "--cd-ui-fokus": DIMACON_COLORS.orange,
        "--cd-ui-interaktion": DIMACON_COLORS.orange,
        "--cd-ui-icon-akzent": DIMACON_COLORS.orange,
        "--cd-ui-wortmarke": DIMACON_COLORS.logo,
      },
    },
    email: {
      logoPath: "/emails/dimacon-logo.png",
      brand: DIMACON_COLORS.dunkel,
      link: DIMACON_COLORS.orangeText,
    },
    og: {
      // The dark sections of dimacon.io
      backgroundColor: DIMACON_COLORS.navyTief,
      backgroundImage: `linear-gradient(135deg, ${DIMACON_COLORS.navy}, ${DIMACON_COLORS.navyTief})`,
      wordmark: DIMACON_COLORS.logo,
      text: WHITE,
      accent: {
        viewBox: DIMACON_ICON_VIEWBOX,
        shapes: DIMACON_ICON_SHAPES,
        color: DIMACON_COLORS.orange,
        width: 340,
        height: 337,
        right: -40,
        bottom: 140,
      },
    },
  },
};

export function isTenantId(value: unknown): value is TenantId {
  return typeof value === "string" && (TENANT_IDS as readonly string[]).includes(value);
}

export function getTenantBranding(tenant?: TenantId | null): TenantBranding {
  return TENANT_BRANDINGS[tenant ?? DEFAULT_TENANT_ID];
}

/** A team's tenant is a setting of the instance admins in the team's metadata; Miragon without one */
export function getTenantIdForTeam(metadata: unknown): TenantId {
  const tenant = metadata && typeof metadata === "object" && "tenant" in metadata ? metadata.tenant : null;
  return isTenantId(tenant) ? tenant : DEFAULT_TENANT_ID;
}

export function getTenantIdForEmail(email: string | null | undefined): TenantId {
  const at = email?.lastIndexOf("@") ?? -1;
  if (!email || at < 0) return DEFAULT_TENANT_ID;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return TENANT_IDS.find((id) => TENANT_BRANDINGS[id].emailDomains.includes(domain)) ?? DEFAULT_TENANT_ID;
}

const declarations = (css: TenantCss) =>
  TENANT_CSS_VARIABLES.map((variable) => `${variable}:${css[variable]};`).join("");

/**
 * The placeholders of every tenant except Miragon. They apply to :root as soon as the page holds a marker
 * (TenantBrandingProvider), so dialogs and menus outside the marked tree follow too; dark mode needs the higher
 * specificity to beat both the tokens' dark roles and the tenant's light values. The selectors need no quotes, so
 * the layouts can render the rules as the text of a style element.
 */
export function buildTenantCss(): string {
  return TENANT_IDS.flatMap((id) => {
    const { css } = TENANT_BRANDINGS[id];
    if (!css) return [];
    return [
      `:root:has([data-tenant=${id}]){${declarations(css.light)}}`,
      `@media screen{:root[data-cd-theme=dark]:has([data-tenant=${id}]){${declarations(css.dark)}}}`,
    ];
  }).join("\n");
}
